import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { heroBanners } from "@/mocks/banners";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const total = heroBanners.length;

  const goTo = useCallback(
    (index: number) => {
      setActive((index + total) % total);
    },
    [total],
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % total);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [total, active]);

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <div className="relative h-[440px] w-full md:h-[560px]">
        {heroBanners.map((banner, index) => (
          <div
            key={banner.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={index !== active}
          >
            <img
              src={banner.image}
              alt={`${banner.title} 焦点图`}
              title={banner.title}
              className="h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 via-foreground-950/45 to-foreground-950/20" />
          </div>
        ))}

        <div className="absolute inset-0 flex items-center">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <div className="max-w-xl">
              {heroBanners.map((banner, index) =>
                index === active ? (
                  <div key={banner.id} className="animate-fade-up">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-background-50">
                      <i className="ri-fire-fill" aria-hidden="true" />
                      {banner.tag}
                    </span>
                    <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
                      {banner.title}
                    </h1>
                    <p className="mt-3 max-w-lg text-sm leading-relaxed text-background-100 md:text-base">
                      {banner.subtitle}
                    </p>
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() => navigate(`/detail/${banner.mediaId}`)}
                        className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-accent-600"
                      >
                        <i className="ri-play-fill text-lg" aria-hidden="true" />
                        立即观看
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate(`/play/${banner.mediaId}`)}
                        className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-5 py-2.5 text-sm font-medium text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20"
                      >
                        <i className="ri-information-line text-lg" aria-hidden="true" />
                        详情介绍
                      </button>
                    </div>
                  </div>
                ) : null,
              )}
            </div>
          </div>
        </div>

        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-3">
          <button
            type="button"
            aria-label="上一张"
            onClick={() => goTo(active - 1)}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/35"
          >
            <i className="ri-arrow-left-s-line text-lg" aria-hidden="true" />
          </button>
          <div className="flex items-center gap-2">
            {heroBanners.map((banner, index) => (
              <button
                key={banner.id}
                type="button"
                aria-label={`切换到第${index + 1}张`}
                onClick={() => goTo(index)}
                className={`h-1.5 rounded-full transition-all ${
                  index === active
                    ? "w-6 bg-accent-500"
                    : "w-1.5 bg-background-50/50 hover:bg-background-50/80"
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => goTo(active + 1)}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/35"
          >
            <i className="ri-arrow-right-s-line text-lg" aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
}